import { ArrowLeft } from '@untitledui/icons'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useHeaderContext } from '@/lib/provider/HeaderContext'
import HeaderProvider from '@/lib/provider/HeaderProvider'
import FadeTransition from '@components/FadeTransition.tsx'
import LastScanLine from '@components/LastScanLine.tsx'
import QuickDaySwitcher from '@components/QuickDaySwitcher.tsx'
import QuickScanSwitcher from '@components/QuickScanSwitcher.tsx'
import QuickStoreSwitcher from '@components/QuickStoreSwitcher.tsx'
import ResponsiveIndicator from '@components/ResponsiveIndicator.tsx'
import Button from '@components/designSystem/Button'
import { DialogProvider } from '@components/designSystem/Dialog/DialogProvider'
import { Headline } from '@components/designSystem/Headline'
import DesktopNavBar from '@components/navbar/DesktopNavBar'
import MobileNavBar from '@components/navbar/MobileNavBar'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import { isTodoPage, useSelectedDay } from '@hooks/useSelectedDay'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { cx } from '@utils/cx'

/**
 * Root layout component that provides dialog context to all routes
 * This ensures dialogs have access to routing context (store ID, scan ID, etc.)
 */
export function RootLayout() {
  return (
    <DialogProvider>
      <HeaderProvider>
        <Outlet />
      </HeaderProvider>
    </DialogProvider>
  )
}

/**
 * A layout component that provides a responsive navigation bar and main content area.
 * On desktop devices, a sidebar navigation is displayed, while on mobile devices, a bottom navigation bar is used.
 */
export function DefaultLayout() {
  const isWideLayout = useIsWideLayout()
  return isWideLayout ? <DesktopLayout /> : <MobileLayout />
}

function DesktopLayout() {
  const { title, isFullPage, showQuickSelection } = useHeaderContext()
  const { day } = useSelectedDay()
  const { pathname } = useLocation()

  // if we have a fullpage layout the header area is floating
  const classNamesHeader = twJoin(
    'flex items-center justify-between',
    !isFullPage && 'pb-4',
    isFullPage && 'absolute inset-x-8 top-8 z-1'
  )

  // no padding if we have a full page layout
  const className = twJoin(
    'relative flex w-full flex-col overflow-hidden bg-bg-primary',
    !isFullPage && 'px-4 py-6 md:px-6 lg:px-8'
  )

  return (
    <div className="flex h-dvh flex-row">
      <DesktopNavBar />
      <div className={className}>
        <header className={classNamesHeader}>
          <Headline title={title}></Headline>

          <div className="flex min-h-10 items-center gap-4">
            <FadeTransition show={showQuickSelection} closeDelay={150} className="flex gap-6">
              <div className='relative'>
                <QuickStoreSwitcher />
                {day && <LastScanLine className='absolute top-full left-3 mt-1' />}
              </div>
              {day ? !isTodoPage(pathname) && <QuickDaySwitcher /> : <QuickScanSwitcher />}
            </FadeTransition>
          </div>
        </header>

        <main className="@container relative min-h-0 w-full flex-1">
          <Outlet />
        </main>
      </div>

      <ResponsiveIndicator />
    </div>
  )
}

function MobileLayout() {
  const { isFullPage } = useHeaderContext()
  const { day } = useSelectedDay()

  const className = twJoin(
    '@container relative w-full flex-1 overflow-hidden bg-bg-primary',
    !isFullPage && 'px-4 pt-4'
  )

  return (
    <div className='relative flex h-dvh flex-col'>
      <main className={className}>
        <Outlet />
      </main>

      {day && <LastScanLine className='bg-bg-primary py-1 text-center' />}
      <MobileNavBar />
      <ResponsiveIndicator />
    </div>
  )
}

/**
 * A layout component that provides a focused view for its child routes.
 * This layout is designed to occupy the full viewport, providing a distraction-free environment.
 *
 * Supports two variants based on `isFullPage` context:
 * - Standard (isFullPage=false): Back button + title in flow, with padding for content
 * - Full-screen (isFullPage=true): Floating back button overlay, full control for children
 */
export function FocusedLayout() {
  const { t } = useTypedTranslation()
  const navigate = useNavigate()
  const { title, isFullPage } = useHeaderContext()

  // Full-screen variant: floating back button, no constraints
  if (isFullPage) {
    return (
      <main className={cx('@container relative h-screen w-screen overflow-hidden bg-map-bg-primary pt-4 pb-0')}>
        <Button
          variant='dark'
          size='lg'
          className="absolute left-3 z-50 size-12 rounded-full"
          onClick={() => navigate(-1)}
          aria-label={t('common.back')}
        >
          <ArrowLeft/>
        </Button>
        <Outlet />
      </main>
    )
  }

  // Standard variant: header in flow with padding & title
  return (
    <main className={cx('@container relative h-screen w-screen overflow-hidden px-4 py-6')}>
      <div className='relative flex items-center justify-center pb-5'>
        <Button
          variant='dark'
          size='lg'
          className="absolute left-0 size-12 rounded-full"
          onClick={() => navigate(-1)}
          aria-label={t('common.back')}
        >
          <ArrowLeft/>
        </Button>
        {title && <Headline title={title} />}
      </div>

      <Outlet />
    </main>
  )
}

/**
 * A layout component that switches between DefaultLayout and FocusedLayout based on the device type.
 * On desktop: Uses DefaultLayout behavior (with navbar)
 * On mobile: Uses FocusedLayout behavior (full-screen, no navbar)
 */
export function ResponsiveLayout() {
  const isWideLayout = useIsWideLayout()
  return isWideLayout ? <DefaultLayout /> : <FocusedLayout />
}
